'use client';

import { type ReactNode, useState } from 'react';

import { cn } from '@/lib/utils';
import { parseHtmlWithStyledLinks } from "@/utils/parseHtmlWithStyledLinks";

import H2 from '@/components/atoms/Typography/H2';
import Section from '@/components/layout/Section';

import Carousel from './components/Carousel';
import CarouselCard from './components/CarouselCard';
import type { CarouselSectionProps } from './CarouselSection.types';

export default function CarouselSection({
    config,
    ...props
}: CarouselSectionProps): ReactNode {
    const [activeSlide, setActiveSlide] = useState(0);
    const { id, title, tiles } = config;

    return (
        <Section
            id={id}
            className="pb-0 sm:pb-0 md:pb-0 xl:pb-0"
            {...props}
        >
            <H2 className="heading-spaces text-center">{title}</H2>

            <Carousel
                activeSlide={activeSlide}
                handleSlideChange={slide => {
                    setActiveSlide(slide);
                }}
            >
                {tiles.map(
                    (
                        { id, image, title, subtitle, content, ...props },
                        index,
                    ) => (
                        <CarouselCard
                            key={id}
                            image={image}
                            title={title}
                            subtitle={subtitle}
                            content={content}
                            className="transition-all"
                            isActive={index === activeSlide}
                            data-test-id={props['data-test-id']}
                        />
                    ),
                )}
            </Carousel>

            <div className="relative h-fit w-full py-3 md:py-6">
                {tiles.map(({ additionalInfo, id }, index) => (
                    <p
                        key={id}
                        className={cn(
                            'hidden w-full text-2xs text-grey-700',
                            activeSlide == index && 'block',
                        )}
                    >
                        {parseHtmlWithStyledLinks(additionalInfo)}
                    </p>
                ))}
            </div>
        </Section>
    );
}
